import { useState, useRef } from 'react';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';

const DIFFERENTIALS = [
  {
    icon: 'ri-user-star-line',
    title: 'Atendimento Personalizado',
    desc: 'Cada cliente recebe atenção exclusiva. Conhecemos a fundo seu caso e oferecemos soluções sob medida para sua realidade.',
  },
  {
    icon: 'ri-time-line',
    title: 'Agilidade nos Processos',
    desc: 'Trabalhamos com eficiência para não perder prazos e garantir que seu processo avance com a velocidade que merece.',
  },
  {
    icon: 'ri-eye-line',
    title: 'Transparência Total',
    desc: 'Comunicação clara e honesta em todas as etapas. Você sempre saberá como está seu processo e quais são as perspectivas.',
  },
  {
    icon: 'ri-shake-hands-line',
    title: 'Compromisso com o Cliente',
    desc: 'Seu sucesso é nossa meta. Dedicação integral, do primeiro contato ao resultado final, com ética e profissionalismo.',
  },
];

function DifferentialsCarousel() {
  const [current, setCurrent] = useState(0);
  const touchStartX = useRef<number>(0);
  const touchEndX = useRef<number>(0);

  const prev = () => setCurrent((c) => (c === 0 ? DIFFERENTIALS.length - 1 : c - 1));
  const next = () => setCurrent((c) => (c === DIFFERENTIALS.length - 1 ? 0 : c + 1));

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e: React.TouchEvent) => {
    touchEndX.current = e.changedTouches[0].clientX;
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 40) {
      if (diff > 0) next();
      else prev();
    }
  };

  const item = DIFFERENTIALS[current];

  return (
    <div className="relative">
      <div
        className="overflow-hidden"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div
          key={current}
          className="text-center p-8 rounded-lg border border-white/15 bg-marrom-escuro/40 transition-all duration-300"
        >
          <div className="w-16 h-16 flex items-center justify-center bg-dourado-principal/15 border border-dourado-principal/25 rounded-full mx-auto mb-6">
            <i className={`${item.icon} text-dourado-principal text-2xl`} />
          </div>
          <h3 className="font-serif text-white text-xl font-semibold mb-3">
            {item.title}
          </h3>
          <p className="font-sans text-white/60 text-sm leading-relaxed">
            {item.desc}
          </p>
          <div className="w-8 h-px bg-dourado-principal mx-auto mt-6" />
        </div>
      </div>

      {/* Arrows */}
      <button
        onClick={prev}
        className="absolute -left-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-marrom-escuro border border-dourado-principal/40 rounded-full text-dourado-principal cursor-pointer"
        aria-label="Anterior"
      >
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-arrow-left-s-line text-lg" />
        </div>
      </button>
      <button
        onClick={next}
        className="absolute -right-3 top-1/2 -translate-y-1/2 w-9 h-9 flex items-center justify-center bg-marrom-escuro border border-dourado-principal/40 rounded-full text-dourado-principal cursor-pointer"
        aria-label="Próximo"
      >
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-arrow-right-s-line text-lg" />
        </div>
      </button>

      {/* Dots */}
      <div className="flex items-center justify-center gap-2 mt-6">
        {DIFFERENTIALS.map((_, i) => (
          <button
            key={i}
            onClick={() => setCurrent(i)}
            className={`rounded-full transition-all duration-300 cursor-pointer ${
              i === current
                ? 'w-6 h-2 bg-dourado-principal'
                : 'w-2 h-2 bg-white/30'
            }`}
            aria-label={`Ir para slide ${i + 1}`}
          />
        ))}
      </div>
    </div>
  );
}

export default function Differentials() {
  const { ref, isVisible } = useScrollAnimation<HTMLElement>();

  return (
    <section className="py-24 lg:py-32 bg-marrom-medio" ref={ref}>
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <div className={`text-center mb-16 reveal ${isVisible ? 'visible' : ''}`}>
          <span className="font-sans text-dourado-claro text-xs tracking-widest uppercase">
            Por que nos escolher
          </span>
          <h2 className="font-serif text-white text-4xl lg:text-5xl font-light mt-3 mb-4">
            Nossos <span className="text-dourado-principal font-semibold">Diferenciais</span>
          </h2>
          <div className="w-14 h-0.5 bg-dourado-principal mx-auto" />
        </div>

        {/* Mobile Carousel */}
        <div className="md:hidden px-4">
          <DifferentialsCarousel />
        </div>

        {/* Desktop Grid */}
        <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {DIFFERENTIALS.map((item, idx) => (
            <div
              key={item.title}
              className={`reveal group text-center p-8 rounded-lg border border-white/15 hover:border-dourado-principal/50 hover:bg-marrom-escuro/40 transition-all duration-400 ${
                isVisible ? 'visible' : ''
              }`}
              style={{ transitionDelay: `${idx * 100}ms` }}
            >
              <div className="w-16 h-16 flex items-center justify-center bg-dourado-principal/15 border border-dourado-principal/25 rounded-full mx-auto mb-6 group-hover:bg-dourado-principal/30 transition-colors duration-300">
                <i className={`${item.icon} text-dourado-principal text-2xl`} />
              </div>
              <h3 className="font-serif text-white text-xl font-semibold mb-3">
                {item.title}
              </h3>
              <p className="font-sans text-white/60 text-sm leading-relaxed">
                {item.desc}
              </p>
              <div className="w-8 h-px bg-dourado-principal mx-auto mt-6 group-hover:w-16 transition-all duration-500" />
            </div>
          ))}
        </div>

        {/* Central CTA Banner */}
        <div className={`mt-16 text-center p-10 rounded-lg bg-marrom-escuro/60 border border-dourado-principal/25 reveal ${isVisible ? 'visible' : ''}`}>
          <h3 className="font-serif text-white text-xl sm:text-3xl lg:text-4xl font-light mb-3">
            Precisa de <span className="text-dourado-principal font-semibold">orientação jurídica</span>?
          </h3>
          <p className="font-sans text-white/60 text-base mb-8 max-w-xl mx-auto">
            Fale agora com uma advogada e receba atendimento especializado. Primeira
            conversa para entender sua situação sem compromisso.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://wa.me/5521974393072?text=Ol%C3%A1!%20Gostaria%20de%20uma%20consulta%20jur%C3%ADdica."
              target="_blank"
              rel="nofollow noreferrer"
              className="inline-flex items-center gap-3 bg-dourado-principal hover:bg-dourado-medio text-marrom-escuro font-sans font-bold text-sm px-8 py-4 rounded-md transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <div className="w-5 h-5 flex items-center justify-center">
                <i className="ri-whatsapp-line text-xl" />
              </div>
              Falar no WhatsApp
            </a>
            <a
              href="#contato"
              onClick={(e) => {
                e.preventDefault();
                document.querySelector('#contato')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="inline-flex items-center gap-2 border border-white/30 hover:border-dourado-principal text-white hover:text-dourado-principal font-sans text-sm px-8 py-4 rounded-md transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              Enviar mensagem
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
